import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spinner, Form, Alert, Modal } from 'react-bootstrap';
import { IconPlus, IconEdit, IconTrash, IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../shared/CurrencyContext';
import { api } from '../../shared/api';
import Select from '../../components/form/AppSelect';
import '../../shared/ReactSelectDarkMode.css';
import { usePermissions } from '../../shared/permissions';
import { useConfirm } from '../../shared/ConfirmContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const VehicleTariffManagement = ({ vehicleId }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();

  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [defaultVatRate, setDefaultVatRate] = useState(null);
  const [kmAmount, setKmAmount] = useState('');
  const [pausalAmount, setPausalAmount] = useState('');

  const emptyForm = {
    name: '',
    tariff_type: null,
    amount: '',
    amount_vat: '',
    valid_from: '',
    valid_to: '',
    priority: 0,
    is_active: true,
    is_promotional: false,
    note: '',
    description: '',
  };

  const [formData, setFormData] = useState(emptyForm);

  const selectTheme = theme => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary: 'var(--tblr-primary)',
      primary75: 'var(--tblr-primary-rgb)',
      primary50: 'var(--tblr-primary-rgb)',
      primary25: 'var(--tblr-primary-rgb)',
      danger: 'var(--tblr-danger)',
      dangerLight: 'var(--tblr-danger-rgb)',
      neutral0: 'var(--tblr-bg-forms)',
      neutral5: 'var(--tblr-bg-surface)',
      neutral10: 'var(--tblr-bg-surface)',
      neutral20: 'var(--tblr-border-color)',
      neutral30: 'var(--tblr-border-color)',
      neutral40: 'var(--tblr-body-color-secondary)',
      neutral50: 'var(--tblr-body-color-secondary)',
      neutral60: 'var(--tblr-body-color-secondary)',
      neutral70: 'var(--tblr-body-color-secondary)',
      neutral80: 'var(--tblr-body-color)',
      neutral90: 'var(--tblr-body-color)',
    },
  });

  useEffect(() => {
    api.get('settings/api/tax/default/')
      .then(response => {
        const rate = parseFloat(response?.rate || response?.data?.rate);
        if (!isNaN(rate)) setDefaultVatRate(rate);
      })
      .catch(() => {});
  }, []);

  const { data: tariffs = [], isLoading: loading } = useQuery({
    queryKey: ['vehicle-tariffs', vehicleId],
    queryFn: async () => {
      const response = await api.get(`transport/api/vehicle-tariffs/?vehicle=${vehicleId}`);
      return Array.isArray(response) ? response : response.results || [];
    },
  });

  const { data: tariffTypes = [] } = useQuery({
    queryKey: ['vehicle-tariff-types-active'],
    queryFn: async () => {
      const response = await api.get('transport/api/vehicle-tariff-types/active/');
      return Array.isArray(response) ? response : response.results || [];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vehicle-tariffs', vehicleId] });

  const getTypeCode = (tariff) => {
    const typeObj = typeof tariff.tariff_type === 'object' ? tariff.tariff_type : tariff.tariff_type_detail || null;
    return typeObj?.code;
  };

  useEffect(() => {
    const kmT = tariffs.find(t => getTypeCode(t) === 'km');
    const pausalT = tariffs.find(t => getTypeCode(t) === 'pausal');
    setKmAmount(kmT ? String(kmT.amount) : '');
    setPausalAmount(pausalT ? String(pausalT.amount) : '');
  }, [tariffs]);

  const patchAmountMutation = useMutation({
    mutationFn: ({ id, amount }) => api.patch(`transport/api/vehicle-tariffs/${id}/`, { amount: parseFloat(amount) }),
    onSuccess: () => invalidate(),
    onError: (err) => setError(err.message || t('common.saveFailed')),
  });

  const handleSaveAmount = (code, value) => {
    const tariff = tariffs.find(t => getTypeCode(t) === code);
    if (!tariff || value === '' || isNaN(parseFloat(value))) return;
    if (parseFloat(tariff.amount) === parseFloat(value)) return;
    patchAmountMutation.mutate({ id: tariff.id, amount: value });
  };

  const saveMutation = useMutation({
    mutationFn: (payload) => editingTariff
      ? api.put(`transport/api/vehicle-tariffs/${editingTariff.id}/`, payload)
      : api.post('transport/api/vehicle-tariffs/', payload),
    onSuccess: () => { handleCloseModal(); invalidate(); },
    onError: (err) => setError(err.message || t('common.saveFailed')),
  });
  const submitting = saveMutation.isPending;

  const deleteMutation = useMutation({
    mutationFn: (tariffId) => api.delete(`transport/api/vehicle-tariffs/${tariffId}/`),
    onSuccess: () => invalidate(),
    onError: (err) => setError(err.message || t('common.deleteFailed')),
  });

  const handleOpenModal = (tariff = null) => {
    if (tariff) {
      setEditingTariff(tariff);
      const tariffTypeId =
        typeof tariff.tariff_type === 'object' && tariff.tariff_type !== null
          ? tariff.tariff_type.id
          : tariff.tariff_type;
      setFormData({
        name: tariff.name || '',
        tariff_type: tariffTypeId || null,
        amount: tariff.amount,
        amount_vat: tariff.amount_vat || '',
        valid_from: tariff.valid_from || '',
        valid_to: tariff.valid_to || '',
        priority: tariff.priority,
        is_active: tariff.is_active,
        is_promotional: tariff.is_promotional,
        note: tariff.note || '',
        description: tariff.description || '',
      });
    } else {
      setEditingTariff(null);
      setFormData(emptyForm);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTariff(null);
    setFormData(emptyForm);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'amount' && defaultVatRate !== null) {
        const net = parseFloat(value);
        if (!isNaN(net)) {
          updated.amount_vat = (net * (1 + defaultVatRate / 100)).toFixed(2);
        } else {
          updated.amount_vat = '';
        }
      }
      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    const payload = {
      vehicle: vehicleId,
      name: formData.name,
      tariff_type: formData.tariff_type,
      amount: parseFloat(formData.amount),
      amount_vat: formData.amount_vat ? parseFloat(formData.amount_vat) : null,
      valid_from: formData.valid_from || null,
      valid_to: formData.valid_to || null,
      priority: parseInt(formData.priority) || 0,
      is_active: formData.is_active,
      is_promotional: formData.is_promotional,
      note: formData.note || '',
      description: formData.description || '',
    };
    saveMutation.mutate(payload);
  };

  const handleDelete = async (tariffId) => {
    if (!await confirm(t('transport.tariffs.confirmDelete'))) return;
    deleteMutation.mutate(tariffId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const isValidityActive = (validFrom, validTo) => {
    const now = new Date();
    const from = validFrom ? new Date(validFrom) : null;
    const to = validTo ? new Date(validTo) : null;
    if (from && to) return now >= from && now <= to;
    if (from) return now >= from;
    if (to) return now <= to;
    return true;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
        <p className="mt-3">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">{t('transport.tariffs.title')}</h3>
        <div className="d-flex gap-2">
          {canManage && (
            <Link to="/transport/tariff-types" className="btn btn-outline-secondary btn-sm">
              {t('transport.tariffs.manageTariffTypes')}
            </Link>
          )}
          {canManage && (
            <Button variant="primary" size="sm" onClick={() => handleOpenModal()}>
              <IconPlus size={16} className="me-1" />
              {t('transport.tariffs.addNew')}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {typeof error === 'object' ? JSON.stringify(error) : error}
        </Alert>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTariff
              ? t('transport.tariffs.editTariff')
              : t('transport.tariffs.addTariff')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('transport.tariffs.name')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('transport.tariffs.namePlaceholder')}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('transport.tariffs.type')} *</Form.Label>
              <Select
                value={
                  formData.tariff_type
                    ? tariffTypes
                        .filter(tp => tp.id === formData.tariff_type)
                        .map(tp => ({ value: tp.id, label: tp.name }))[0] || null
                    : null
                }
                onChange={(option) => handleInputChange('tariff_type', option ? option.value : null)}
                options={tariffTypes.map(tp => ({ value: tp.id, label: tp.name }))}
                theme={selectTheme}
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                placeholder={t('transport.tariffs.selectType')}
                required
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('transport.tariffs.amount')} *</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('transport.tariffs.amountVat')}</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    min="0"
                    value={formData.amount_vat}
                    onChange={(e) => handleInputChange('amount_vat', e.target.value)}
                    placeholder="0.00"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('transport.tariffs.validFrom')}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.valid_from}
                    onChange={(e) => handleInputChange('valid_from', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    {t('transport.tariffs.validFromHelp')}
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t('transport.tariffs.validTo')}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.valid_to}
                    onChange={(e) => handleInputChange('valid_to', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    {t('transport.tariffs.validToHelp')}
                  </Form.Text>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>{t('transport.tariffs.priority')}</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
              />
              <Form.Text className="text-muted">
                {t('transport.tariffs.priorityHelp')}
              </Form.Text>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={t('transport.tariffs.isActive')}
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={t('transport.tariffs.isPromotional')}
                    checked={formData.is_promotional}
                    onChange={(e) => handleInputChange('is_promotional', e.target.checked)}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>{t('transport.tariffs.description')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('transport.tariffs.descriptionPlaceholder')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('transport.tariffs.note')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                placeholder={t('transport.tariffs.notePlaceholder')}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <IconCheck size={16} className="me-1" />
                  {t('common.save')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default VehicleTariffManagement;
